import type { MonitorEventError } from '../types';
import { IntegrationIdMap } from '../utils';
import type { IntegrationErrorContent, MonitorEvent } from './types';

type Integration = (typeof IntegrationIdMap)[string][string];

// Shown to end users when an integration needs action.
// Raw monitor error messages/status codes are technical and must never be
// rendered on the status page — they stay on the detail page's event log.
//
// Every failing event's errors are matched against the rules below, in
// array order — keep specific rules before generic catch-alls. The rules
// are consolidated from a 90-day Datadog analysis of production errors
// (Pigello_Integration_Errors.xlsx, aug 2026); parameterized parts of the
// messages (account numbers, names, dates) are generalized in the regexes.
// Adding a new error is a rule addition here.
//
// The rules marked "Hidden" are commented out rather than deleted, so
// re-enabling one is uncommenting it. A hidden error matches no rule, and
// there is no fallback copy any more — the event then counts as benign (see
// isEventBenign), so the integration stays green and neither the status page
// nor the dashboard banner mentions it. The raw message is still on the
// detail page's event log.

type CatalogRule = {
  statusCode?: string;
  messagePattern?: RegExp;
  entry: IntegrationErrorContent;
};

/* Hidden: the fallback is no longer shown. An error matching none of the
   rules below gets no customer-facing copy at all — see isEventBenign.
const GENERIC_FALLBACK: IntegrationErrorContent = {
  title: 'Synkroniseringen misslyckades',
  description:
    'Något gick fel i synkroniseringen. Öppna loggarna för att se detaljer och försöka igen.',
  steps: [
    'Läs det tekniska felmeddelandet i händelseloggen — det pekar oftast ut vad som gick fel.',
    'Åtgärda det som felmeddelandet pekar på, t.ex. en uppgift som saknas i Pigello eller en inställning i det externa systemet.',
    'Klicka på ”Jag har åtgärdat — kontrollera igen” för att köra om händelserna.',
    'Kontakta supporten om felet kvarstår eller om meddelandet är svårt att tyda.',
  ],
};
*/

// SIE4, GL02 and GL07 are SFTP file exports on the same application and
// share the exact same failure modes.
const SFTP_EXPORT_RULES: CatalogRule[] = [
  /* Hidden — not shown to the customer.
  {
    messagePattern: /could not create a bookkeeping file/i,
    entry: {
      title: 'Bokföringsfil kunde inte skapas',
      description: 'Exportfilen (SIE/GL) för överföring kunde inte genereras.',
      steps: [
        'Kontrollera att bokföringsunderlaget är komplett (verifikationer, konton och period).',
        'Försök igen.',
      ],
    },
  },
  */
  {
    messagePattern: /could not establish an ssh session/i,
    entry: {
      title: 'Ingen SSH/SFTP-anslutning',
      description:
        'Det gick inte att upprätta en SSH-session mot mottagande server.',
      steps: [
        'Kontrollera anslutningen till mottagande server (adress, port och brandvägg).',
        'Försök igen — kontakta din IT-kontakt om problemet kvarstår.',
      ],
    },
  },
  {
    messagePattern: /connection with ssh was not accepted/i,
    entry: {
      title: 'SSH-anslutning nekades',
      description:
        'Mottagande server accepterade inte SSH-anslutningen (autentisering/nycklar).',
      steps: [
        'Kontrollera inloggningsuppgifterna/SSH-nyckeln mot mottagaren och att nyckeln är registrerad hos dem.',
        'Uppdatera uppgifterna och försök igen.',
      ],
    },
  },
];

/* Hidden — not shown to the customer.
// Payment posting errors shared by Bankgiro/bank connections and Autogiro.
const DUPLICATE_PAYMENT_RULES: CatalogRule[] = [
  {
    messagePattern: /uniqueness.*(1344|external_transaction_id)/i,
    entry: {
      title: 'Betalningen är redan registrerad',
      description:
        'Betalningen har ett transaktions-ID som redan finns i Pigello — den är alltså redan registrerad.',
      steps: ['Ingen åtgärd behövs. Betalningen finns redan i Pigello.'],
      benign: true,
    },
  },
  {
    messagePattern: /uniqueness constraint \(1027\)/i,
    entry: {
      title: 'Betalningen är redan registrerad',
      description:
        'Betalningen finns redan i Pigello — ett unikt värde krockar med en befintlig post.',
      steps: [
        'Ingen åtgärd behövs, men kontrollera att betalningen inte skickas två gånger.',
      ],
      benign: true,
    },
  },
];
*/

const FORTNOX_AUTH_EXPIRED: IntegrationErrorContent = {
  title: 'Anslutningen till Fortnox har gått ut',
  description:
    'Behörigheten mot Fortnox har gått ut och kunde inte förnyas. Bokföringssynken är pausad tills anslutningen förnyas — inga verifikat går förlorade, de skickas så fort anslutningen är på plats igen.',
  stepsHeading: 'Så här förnyar du anslutningen till Fortnox',
  steps: [
    'Öppna integrationsinställningarna för Fortnox i Pigello.',
    'Stäng av Fortnox-kopplingen och slå på den igen.',
    'Godkänn behörigheterna som Pigello begär när Fortnox frågar.',
    'Återvänd hit — synken återupptas automatiskt inom någon minut.',
  ],
};

const VISMA_AUTH_EXPIRED: IntegrationErrorContent = {
  title: 'Anslutningen till Visma har gått ut',
  description:
    'Behörigheten mot Visma har gått ut och behöver förnyas. Synken är pausad tills anslutningen är på plats igen.',
  stepsHeading: 'Så här förnyar du anslutningen till Visma',
  steps: [
    'Öppna integrationsinställningarna för Visma i Pigello.',
    'Stäng av kopplingen och slå på den igen.',
    'Godkänn behörigheterna som Pigello begär när Visma frågar.',
  ],
};

const RULES_BY_INTEGRATION: Partial<Record<Integration, CatalogRule[]>> = {
  FORTNOX: [
    {
      statusCode: '401',
      messagePattern:
        /could not sync access-token|unauthorized|invalid.*(token|grant)|expired/i,
      entry: FORTNOX_AUTH_EXPIRED,
    },
    {
      messagePattern: /no authentication data available/i,
      entry: {
        title: 'Ingen Fortnox-autentisering',
        description:
          'Det finns inga sparade inloggningsuppgifter mot Fortnox för kopplingen.',
        steps: [
          'Aktivera Fortnox-integrationen och logga in mot Fortnox så att kopplingen får behörighet.',
          'Godkänn åtkomsten när Fortnox frågar.',
        ],
      },
    },
    /* Hidden — not shown to the customer.
    {
      messagePattern: /another invocation is still refreshing/i,
      entry: {
        title: 'Fortnox-token förnyas — försök igen',
        description:
          'En annan körning håller på att förnya Fortnox-token samtidigt (tillfällig låsning).',
        steps: [
          'Du behöver inte göra något — ett nytt försök sker automatiskt. Vänta en stund och försök igen om det behövs.',
        ],
        benign: true,
      },
    },
    */
    {
      messagePattern: /could not sync oauth credentials/i,
      entry: {
        title: 'Fel authorization-code mot Fortnox',
        description:
          'OAuth-uppgifterna mot Fortnox kunde inte synkas — authorization-koden verkar felaktig.',
        steps: [
          'Starta kopplingen mot Fortnox på nytt och godkänn åtkomsten i Fortnox igen.',
        ],
      },
    },
    {
      // Both 'Konto 9090 saknas i kontoplanen' and the bare 'Konto 9202
      // saknas' occur in production messages.
      messagePattern: /konto \d+ saknas( i kontoplanen)?/i,
      entry: {
        title: 'Konto saknas i Fortnox kontoplan',
        description:
          'Verifikationen kunde inte bokföras i Fortnox: ett konto saknas i kontoplanen. Vilket konto det gäller syns i felmeddelandet.',
        steps: [
          'Logga in i Fortnox och lägg till kontot i din kontoplan.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /konto \d+ är inte aktivt/i,
      entry: {
        title: 'Konto inaktivt i Fortnox',
        description:
          'Verifikationen kunde inte bokföras i Fortnox: ett konto är inaktivt. Vilket konto det gäller syns i felmeddelandet.',
        steps: [
          'Logga in i Fortnox och aktivera kontot.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /kostnadsställe (är )?ej tillåtet på konto/i,
      entry: {
        title: 'Kostnadsställe ej tillåtet på konto',
        description:
          'Verifikationen kunde inte bokföras i Fortnox: kostnadsställe är inte tillåtet på ett av kontona. Vilket konto det gäller syns i felmeddelandet.',
        steps: [
          'Logga in i Fortnox och tillåt kostnadsställe på kontot, eller ta bort kostnadsstället från verifikationen.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /har låst perioden|automatlåst period/i,
      entry: {
        title: 'Perioden är låst i Fortnox',
        description:
          'Verifikationen kunde inte bokföras i Fortnox: bokföringsperioden är låst.',
        steps: [
          'Logga in i Fortnox och lås upp perioden, eller ändra bokföringsdatumet till en öppen period.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /verifikationsserien är inte manuell/i,
      entry: {
        title: 'Verifikationsserien är inte manuell',
        description:
          'Verifikationen kunde inte bokföras i Fortnox: verifikationsserien är inte manuell.',
        steps: [
          'Logga in i Fortnox och ändra verifikationsserien till typen Manuell.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /ogiltig verifikationsserie/i,
      entry: {
        title: 'Ogiltig verifikationsserie',
        description:
          'Verifikationen kunde inte bokföras i Fortnox: ogiltig verifikationsserie angiven.',
        steps: [
          'Logga in i Fortnox och välj eller skapa en giltig, manuell verifikationsserie.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /innehåller ej tillåtna tecken/i,
      entry: {
        title: 'Otillåtna tecken i verifikationstext',
        description:
          'Verifikationen kunde inte bokföras i Fortnox: texten innehåller tecken som Fortnox inte tillåter.',
        steps: [
          'Ta bort specialtecken i texten/benämningen.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /verifikationen balanserar inte/i,
      entry: {
        title: 'Verifikationen balanserar inte',
        description:
          'Verifikationen kunde inte bokföras i Fortnox: debet och kredit är inte lika stora.',
        steps: [
          'Kontrollera att debet och kredit balanserar.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /fortnox request timed out/i,
      entry: {
        title: 'Timeout mot Fortnox',
        description:
          'Anropet mot Fortnox svarade inte i tid — ett tillfälligt fel.',
        steps: ['Vänta en stund och försök igen.'],
      },
    },
    // Any other 401 is still an auth problem, whatever Fortnox called it.
    { statusCode: '401', entry: FORTNOX_AUTH_EXPIRED },
    // Catch-all for verification syncs without a specific vendor reason —
    // must stay last so the specific rules above win.
    {
      messagePattern: /failed to sync \d+ verification/i,
      entry: {
        title: 'Verifikationer kunde inte synkas till Fortnox',
        description:
          'En eller flera verifikationer kunde inte bokföras i Fortnox (ingen specifik orsak angavs).',
        steps: [
          'Kontrollera kontering och bokföringsperiod i Fortnox och försök igen.',
          'Kontakta supporten om felet kvarstår.',
        ],
      },
    },
  ],
  VISMA_EEKONOMI: [
    {
      messagePattern: /no fiscal year for/i,
      entry: {
        title: 'Räkenskapsår saknas i Visma',
        description:
          'Det finns inget räkenskapsår i Visma som täcker verifikationens bokföringsdatum. Vilket datum det gäller syns i felmeddelandet.',
        steps: [
          'Skapa eller öppna räkenskapsåret för datumet i Visma. Tänk på att räkenskapsår måste läggas till i följd.',
          'Försök igen när året finns på plats.',
        ],
      },
    },
    {
      messagePattern: /could not find an account with number/i,
      entry: {
        title: 'Konto saknas i Visma',
        description:
          'Ett konto finns inte i räkenskapsårets kontoplan i Visma. Vilket konto det gäller syns i felmeddelandet.',
        steps: [
          'Lägg till kontot i kontoplanen för aktuellt räkenskapsår i Visma.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /period.*(closed|locked|stängd|låst)/i,
      entry: {
        title: 'Bokföringsperioden är stängd i Visma',
        description:
          'Bokföringsperioden är stängd i Visma. Öppna perioden och synka transaktionerna.',
        steps: ['Öppna bokföringsperioden i Visma.', 'Försök därefter igen.'],
      },
    },
    {
      messagePattern: /visma request timed out/i,
      entry: {
        title: 'Timeout mot Visma',
        description:
          'Anropet mot Visma svarade inte i tid — ett tillfälligt fel.',
        steps: ['Vänta en stund och försök igen.'],
      },
    },
    {
      messagePattern: /could not prepare connections/i,
      entry: {
        title: 'Kopplingen mot Visma misslyckades',
        description: 'Pigello kunde inte upprätta kopplingen mot Visma.',
        steps: [
          'Kontrollera att din Visma-koppling är aktiv och återaktivera den vid behov.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      statusCode: '401',
      messagePattern: /unauthorized|invalid.*(token|grant)|expired/i,
      entry: VISMA_AUTH_EXPIRED,
    },
    {
      messagePattern: /exception of type 'system\.exception'/i,
      entry: {
        title: 'Oväntat fel från Visma',
        description:
          'Visma returnerade ett generiskt serverfel utan tydlig orsak.',
        steps: [
          'Försök igen.',
          'Kontrollera verifikationens datum, konton och moms om felet kvarstår, och kontakta supporten vid behov.',
        ],
      },
    },
    // Any other 401 is still an auth problem, whatever Visma called it.
    { statusCode: '401', entry: VISMA_AUTH_EXPIRED },
    // Catch-all — must stay last so the specific rules above win.
    {
      messagePattern: /failed to sync \d+ verification/i,
      entry: {
        title: 'Verifikationer kunde inte synkas till Visma',
        description:
          'Verifikationer kunde inte bokföras i Visma (ingen specifik orsak angavs).',
        steps: [
          'Kontrollera räkenskapsår, konton och datum i Visma och försök igen.',
        ],
      },
    },
  ],
  SCRIVE: [
    /* Hidden — not shown to the customer.
    {
      messagePattern: /mobile field|field.?mobile|type of field: mobile/i,
      entry: {
        title: 'Ogiltigt mobilnummer',
        description:
          'Ett mobilnummer för en part är ogiltigt och krävs för leverans/signering via SMS.',
        steps: [
          'Ange partens mobilnummer med landskod (t.ex. +46701234567).',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /no_signing_party|has no signing party/i,
      entry: {
        title: 'Ingen signeringspart',
        description: 'Dokumentet saknar en part som ska signera.',
        steps: [
          'Lägg till minst en part som ska signera dokumentet.',
          'Starta signeringen igen.',
        ],
      },
    },
    {
      messagePattern: /approver.*field.placement/i,
      entry: {
        title: 'Godkännare kan inte ha fält',
        description:
          'En godkännare har tilldelats fält i dokumentet, vilket inte är tillåtet i Scrive.',
        steps: [
          'Ta bort fältplaceringarna för godkännaren, eller ändra parten till signerande.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /ssn-number field/i,
      entry: {
        title: 'Personnummerfält krävs',
        description:
          'Ett personnummerfält med värde måste finnas på grund av vald leverans-/signeringsmetod.',
        steps: [
          'Lägg till ett personnummerfält med giltigt värde för parten, eller byt leveransmetod.',
          'Försök därefter igen.',
        ],
      },
    },
    {
      messagePattern: /\(text\) field for participant/i,
      entry: {
        title: 'Ogiltigt textfältvärde',
        description: 'Värdet i ett textfält för en part är ogiltigt.',
        steps: ['Rätta värdet i textfältet för parten och försök igen.'],
      },
    },
    {
      messagePattern: /days to sign/i,
      entry: {
        title: 'Signeringstiden kan bara ökas',
        description: 'Antalet dagar att signera kan bara ökas, inte minskas.',
        steps: [
          'Ange lika många eller fler dagar att signera än tidigare och försök igen.',
        ],
      },
    },
    {
      messagePattern: /could not upload document-file/i,
      entry: {
        title: 'Uppladdning till Scrive misslyckades',
        description: 'Dokumentfilen kunde inte laddas upp till Scrive.',
        steps: [
          'Kontrollera att dokumentet är en giltig PDF (ej skadad) och försök igen.',
        ],
      },
    },
    {
      messagePattern: /connection between the documents in pigello and scrive/i,
      entry: {
        title: 'Dokumentkoppling saknas',
        description:
          'Det finns ingen koppling mellan dokumentet i Pigello och Scrive, vilket krävs för händelsen.',
        steps: [
          'Starta signeringen på nytt så att dokumentet kopplas mot Scrive igen.',
          'Kontrollera att dokumentet inte har tagits bort i Scrive.',
        ],
      },
    },
    {
      messagePattern: /could not find the document in pigello/i,
      entry: {
        title: 'Dokument saknas i Pigello',
        description:
          'Dokumentet (eller kopplingen till Scrive) kunde inte hittas i Pigello.',
        steps: [
          'Kontrollera att dokumentet finns kvar och starta om signeringsflödet.',
        ],
      },
    },
    {
      messagePattern: /could not update parties/i,
      entry: {
        title: 'Kunde inte uppdatera parter',
        description:
          'Statusdata för dokumentets parter kunde inte uppdateras mot Scrive.',
        steps: [
          'Kontrollera att dokumentet finns i Scrive och försök igen.',
          'Starta om signeringen om felet kvarstår.',
        ],
      },
    },
    {
      messagePattern: /could not collect all parties/i,
      entry: {
        title: 'Kunde inte hämta parter',
        description: 'Alla parter kopplade till dokumentet kunde inte hämtas.',
        steps: [
          'Försök igen.',
          'Kontrollera dokumentets parter i Scrive om felet kvarstår.',
        ],
      },
    },
    {
      messagePattern: /could not store the complete evidence file/i,
      entry: {
        title: 'Kunde inte spara bevisfil i Pigello',
        description:
          'Den kompletta bevisfilen och slutdatum kunde inte sparas i Pigello.',
        steps: ['Försök igen.', 'Kontakta supporten om felet kvarstår.'],
      },
    },
    {
      messagePattern: /evidence file from scrive/i,
      entry: {
        title: 'Kunde inte hämta bevisfil',
        description: 'Den kompletta bevisfilen kunde inte hämtas från Scrive.',
        steps: [
          'Kontrollera att dokumentet är färdigsignerat i Scrive och försök igen.',
        ],
      },
    },
    {
      messagePattern: /auto-reminder/i,
      entry: {
        title: 'Kunde inte sätta påminnelse',
        description: 'Auto-påminnelsevärdet kunde inte uppdateras i Scrive.',
        steps: ['Kontrollera påminnelseinställningen och försök igen.'],
      },
    },
    // Catch-alls — must stay last so the specific rules above win.
    {
      messagePattern:
        /unknown error ocurred|could not upload document information/i,
      entry: {
        title: 'Fel mot Scrive',
        description: 'Ett fel uppstod i kommunikationen med Scrive.',
        steps: ['Försök igen.', 'Kontakta supporten om felet kvarstår.'],
      },
    },
    */
  ],
  ECO_POST: [
    /* Hidden — not shown to the customer.
    {
      messagePattern: /could not find ebank recipient info/i,
      entry: {
        title: 'E-fakturauppgift saknas för mottagare',
        description:
          'Ingen e-faktura-/bankinfo hittades för mottagarens personnummer.',
        steps: [
          'Kontrollera att mottagaren har rätt personnummer och är ansluten för e-faktura.',
          'Komplettera uppgifterna och försök igen.',
        ],
      },
    },
    {
      messagePattern: /require peppol id/i,
      entry: {
        title: 'PEPPOL-ID saknas',
        description:
          'E-fakturadistribution kräver att ett PEPPOL-ID är angivet.',
        steps: [
          'Ange mottagarens PEPPOL-ID (t.ex. 0007:5592812345).',
          'Kontrollera att mottagaren är registrerad för PEPPOL och försök igen.',
        ],
      },
    },
    {
      messagePattern: /require iban, bic/i,
      entry: {
        title: 'Bankuppgifter saknas för e-faktura',
        description:
          'E-faktura kräver att IBAN, BIC och kontonummer är angivna.',
        steps: ['Fyll i IBAN, BIC och kontonummer och försök igen.'],
      },
    },
    {
      messagePattern: /missing sender id for kivra/i,
      entry: {
        title: 'Kivra avsändar-ID saknas',
        description: 'Avsändar-ID för Kivra är inte konfigurerat.',
        steps: [
          'Ange avsändar-ID för Kivra i inställningarna och försök igen.',
        ],
      },
    },
    {
      messagePattern: /missing sender fui/i,
      entry: {
        title: 'Kivra FUI saknas',
        description: 'Avsändarens FUI (Kivra-registrering) saknas.',
        steps: [
          'Ange avsändarens FUI för Kivra i inställningarna och försök igen.',
        ],
      },
    },
    {
      messagePattern: /kivra invoice.*total_owed/i,
      entry: {
        title: 'Kivra-faktura saknar belopp',
        description: 'Fakturan mot Kivra saknar giltigt totalbelopp.',
        steps: [
          'Kontrollera att fakturans totalbelopp är ifyllt och försök igen.',
        ],
      },
    },
    {
      messagePattern: /kivra address.*recipient ssn/i,
      entry: {
        title: 'Mottagarens personnummer saknas (Kivra)',
        description:
          'Kivra-adressen kunde inte byggas — mottagarens personnummer saknas eller är ogiltigt.',
        steps: ['Ange mottagarens personnummer och försök igen.'],
      },
    },
    {
      messagePattern: /missing vat code/i,
      entry: {
        title: 'Momskod saknas',
        description: 'Momskod (VAT code) saknas på posten.',
        steps: ['Ange momskod på fakturaraden och försök igen.'],
      },
    },
    // Catch-all — must stay last so the specific rules above win.
    {
      messagePattern: /missing or invalid: recipient/i,
      entry: {
        title: 'Mottagaruppgift saknas',
        description:
          'Obligatorisk mottagarinformation saknas eller är ogiltig.',
        steps: ['Fyll i mottagarens uppgifter och försök igen.'],
      },
    },
    */
  ],
  BANKGIRO: [
    {
      messagePattern: /credentials are not authorized/i,
      entry: {
        title: 'Behörighet saknas mot banken',
        description:
          'Integrationens inloggningsuppgifter är inte längre behöriga mot banken/Bankgirot.',
        stepsHeading: 'Så här förnyar du behörigheten',
        steps: [
          'Öppna integrationsinställningarna i Pigello.',
          'Avaktivera kopplingen och aktivera den igen så att behörigheten förnyas.',
        ],
      },
    },
    /* Hidden — not shown to the customer.
    ...DUPLICATE_PAYMENT_RULES,
    {
      messagePattern: /unexpected api server error/i,
      entry: {
        title: 'Oväntat serverfel',
        description: 'Ett oväntat serverfel uppstod — ett tillfälligt fel.',
        steps: ['Försök igen senare.'],
      },
    },
    */
  ],
  AUTOGIRO: [
    {
      messagePattern: /multiple payment methods with same customer number/i,
      entry: {
        title: 'Dubblett på kundnummer',
        description:
          'Flera betalsätt/medgivanden hittades med samma kundnummer i Autogiro.',
        steps: [
          'Se till att varje kund bara har ett aktivt Autogiro-medgivande per kundnummer.',
          'Ta bort dubbletter och försök igen.',
        ],
      },
    },
    /* Hidden — not shown to the customer.
    ...DUPLICATE_PAYMENT_RULES,
    */
    {
      messagePattern: /failed to connect to sftp/i,
      entry: {
        title: 'Ingen SFTP-anslutning',
        description: 'Det fanns ingen aktiv SFTP-session vid överföringen.',
        steps: ['Kontrollera anslutningen och försök igen.'],
      },
    },
    {
      messagePattern: /unexpected server error/i,
      entry: {
        title: 'Oväntat serverfel',
        description: 'Ett oväntat serverfel uppstod — ett tillfälligt fel.',
        steps: ['Försök igen om en stund.'],
      },
    },
  ],
  PS_FINANCE: [
    /* Hidden — not shown to the customer.
    {
      messagePattern: /could not retrieve invoice case/i,
      entry: {
        title: 'Fakturaärende saknas i PS Finance',
        description: 'Fakturaärendet kunde inte hämtas från PS Finance.',
        steps: [
          'Kontrollera att ärendet finns i PS Finance och försök igen om en stund.',
        ],
      },
    },
    */
    {
      messagePattern: /integration was not setup correctly/i,
      entry: {
        title: 'Integrationen är inte korrekt aktiverad',
        description:
          'PS Finance-integrationen är inte korrekt uppsatt — aktiveringshändelser har inte körts klart.',
        steps: [
          'Kör aktiveringen av PS Finance-integrationen på nytt.',
          'Kontrollera att den slutförs utan fel.',
        ],
      },
    },
    {
      messagePattern: /could not create customer/i,
      entry: {
        title: 'Kund kunde inte skapas i PS Finance',
        description: 'Kunden kunde inte skapas i PS Finance.',
        steps: [
          'Kontrollera att kundens uppgifter (person-/orgnr, namn och adress) är kompletta och försök igen.',
        ],
      },
    },
    /* Hidden — not shown to the customer.
    {
      messagePattern: /ps finance server related error/i,
      entry: {
        title: 'Serverfel hos PS Finance',
        description:
          'Anropet misslyckades på grund av ett tillfälligt serverfel hos PS Finance.',
        steps: ['Försök igen senare.'],
      },
    },
    {
      messagePattern:
        /could not retrieve (debt invoices|existing debt invoice events)/i,
      entry: {
        title: 'Kunde inte hämta data i Pigello',
        description:
          'Skuld-/inkassofakturor eller händelser kunde inte hämtas i Pigello.',
        steps: ['Tillfälligt fel — försök igen om en stund.'],
      },
    },
    {
      messagePattern: /could not create debt invoice events/i,
      entry: {
        title: 'Kunde inte skapa händelser i Pigello',
        description: 'Skuldfakturahändelser kunde inte skapas i Pigello.',
        steps: [
          'Försök igen.',
          'Kontrollera fakturauppgifterna om felet kvarstår.',
        ],
      },
    },
    */
  ],
  SENDGRID: [
    /* Hidden — not shown to the customer.
    {
      messagePattern: /could not update delivery information/i,
      entry: {
        title: 'Saknar behörighet till instanser',
        description:
          'Leveransinformation kunde inte uppdateras — kontot saknar behörighet till angivna instanser i Pigello.',
        steps: [
          'Kontrollera att kontot har behörighet till de aktuella objekten/organisationerna.',
          'Saknas åtkomst, kontakta din administratör och försök sedan igen.',
        ],
      },
    },
    {
      messagePattern: /could not initate sending of email/i,
      entry: {
        title: 'E-post kunde inte skickas',
        description: 'Utskicket av e-post kunde inte initieras.',
        steps: [
          'Kontrollera avsändar- och mottagaruppgifterna och försök skicka igen.',
        ],
      },
    },
    */
  ],
  INFOMETRIC: [
    {
      messagePattern: /pigello session unauthorized/i,
      entry: {
        title: 'Pigello-token ogiltig',
        description:
          'Sessionen mot Pigello var inte behörig vid mät-/sensorsynk — integrationstoken kan vara ogiltig eller utgången.',
        steps: [
          'Återaktivera Infometric-kopplingen i integrationsinställningarna så att en ny behörighet skapas.',
        ],
      },
    },
    {
      messagePattern: /infometric login failed/i,
      entry: {
        title: 'Fel inloggning mot Infometric',
        description: 'Användarnamn eller lösenord mot Infometric är felaktigt.',
        steps: [
          'Uppdatera användarnamn och lösenord för Infometric i integrationsinställningarna.',
          'Försök därefter igen.',
        ],
      },
    },
  ],
  SIE4: SFTP_EXPORT_RULES,
  Gl02: SFTP_EXPORT_RULES,
  Gl07: SFTP_EXPORT_RULES,
};

function ruleMatches(rule: CatalogRule, error: MonitorEventError): boolean {
  if (rule.statusCode && rule.messagePattern) {
    return (
      rule.statusCode === error.context?.status_code &&
      rule.messagePattern.test(error.message ?? '')
    );
  }
  if (rule.statusCode) {
    return rule.statusCode === error.context?.status_code;
  }
  if (rule.messagePattern) {
    return rule.messagePattern.test(error.message ?? '');
  }
  return false;
}

export type CatalogErrorGroup = {
  entry: IntegrationErrorContent;
  events: MonitorEvent[];
};

function rulesFor(
  applicationId: string,
  serviceId: string
): CatalogRule[] | undefined {
  const integration = IntegrationIdMap[applicationId]?.[serviceId];
  return integration ? RULES_BY_INTEGRATION[integration] : undefined;
}

function matchEntriesForEvent(
  rules: CatalogRule[] | undefined,
  evt: MonitorEvent
): Set<IntegrationErrorContent> {
  const entries = new Set<IntegrationErrorContent>();
  for (const error of evt.errors ?? []) {
    const rule = rules?.find((candidate) => ruleMatches(candidate, error));
    if (rule) {
      entries.add(rule.entry);
    }
  }
  return entries;
}

// True when everything this event matched is an expected outcome. An event that
// also names a real problem is not benign, so a mixed message still counts.
// An event that matches no rule at all is benign too: there is no longer a
// fallback copy, so there is nothing to show the user and nothing to act on.
// The empty set makes .every() vacuously true — that is deliberate, not a slip.
export function isEventBenign(
  applicationId: string,
  serviceId: string,
  evt: MonitorEvent
): boolean {
  const entries = matchEntriesForEvent(rulesFor(applicationId, serviceId), evt);
  return [...entries].every((entry) => entry.benign === true);
}

export function matchCatalogErrorGroups(
  applicationId: string,
  serviceId: string,
  failingEvents: MonitorEvent[]
): CatalogErrorGroup[] {
  const rules = rulesFor(applicationId, serviceId);

  const groups = new Map<IntegrationErrorContent, MonitorEvent[]>();
  for (const evt of failingEvents) {
    for (const entry of matchEntriesForEvent(rules, evt)) {
      if (entry.benign) continue;
      const events = groups.get(entry);
      if (events) {
        events.push(evt);
      } else {
        groups.set(entry, [evt]);
      }
    }
  }

  return [...groups.entries()].map(([entry, events]) => ({ entry, events }));
}
